import { HttpClient, HttpHeaders } from '@angular/common/http';
import { DestroyRef, Injectable } from '@angular/core';
import { catchError, concat, exhaustMap, from, interval, lastValueFrom, of } from 'rxjs';
import {
  AppUpdate,
  AppUpdateAvailability,
  AppUpdateResultCode,
  FlexibleUpdateInstallStatus,
} from '@capawesome/capacitor-app-update';
import { Capacitor } from '@capacitor/core';
import { ModalController } from '@ionic/angular';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AppUpdateModalComponent,
  ConfirmationModalComponent,
  TAppUpdateModalComponentProps,
  TConfirmationModalComponentProps,
  TConfirmationModalDimissEmitVal,
} from '@monorepo/ui';
import { MmEnvironmentService } from './machine-maintenance/mm-environment.service';
import { DISABLE_ERROR_ALERT_HEADER } from '@monorepo/types';

export type TRESPONSE_APP_UPDATE_MIN_REQUIRED_VERSION = {
  minRequiredAppVersion: string;
};
const DEFAULT_MIN_REQUIRED_VERSION: Record<TAppUpdateModalComponentProps['appName'], string> = {
  Maintaini8: '7.0.3',
};

@Injectable({ providedIn: 'root' })
export class AppUpdateService {
  #platform = Capacitor.getPlatform() as 'android' | 'ios' | 'web';
  #app: TAppUpdateModalComponentProps['appName'] = 'Maintaini8';

  constructor(
    private http: HttpClient,
    private modalController: ModalController,
    private destroyRef: DestroyRef,
    private mmEnvironmentService: MmEnvironmentService,
  ) {}

  async init(app: TAppUpdateModalComponentProps['appName']) {
    if (this.#platform === 'web') return;

    this.#app = app;
    const url = this.#getUrl();

    concat(
      from(this.#checkForUpdate(url, DEFAULT_MIN_REQUIRED_VERSION[app], true)),
      interval(1_000 * 60 * 60).pipe(
        exhaustMap(() => from(this.#checkForUpdate(url, DEFAULT_MIN_REQUIRED_VERSION[app]))),
      ),
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();

    if (this.#platform === 'android')
      AppUpdate.addListener('onFlexibleUpdateStateChange', async (state) => {
        if (state.installStatus === FlexibleUpdateInstallStatus.DOWNLOADED) {
          const componentProps: TConfirmationModalComponentProps = {
            header: 'Update ready',
            contentTxts: [
              'A new version of the app has been downloaded.',
              'Install it now for the latest features and improvements.',
            ],
            primaryBtnTxt: 'Install now',
            closeModalBtnTxt: 'Install on restart',
            isMobile: true,
          };
          const modal = await this.modalController.create({
            component: ConfirmationModalComponent,
            componentProps,
          });
          await modal.present();

          const { role } = (await modal.onDidDismiss()) as TConfirmationModalDimissEmitVal;
          if (role === 'SAVE') AppUpdate.completeFlexibleUpdate();
        }
      });
  }

  async #checkForUpdate(url: string, defaultMinRequiredVersion: string, askForFlexibleUpdates: boolean = false) {
    try {
      const {
        currentVersionName,
        updateAvailability,
        availableVersionName: iosAvailableVersionName,
      } = await AppUpdate.getAppUpdateInfo();

      if (updateAvailability === AppUpdateAvailability.UPDATE_AVAILABLE) {
        const { minRequiredAppVersion } = await lastValueFrom(
          this.http
            .get<TRESPONSE_APP_UPDATE_MIN_REQUIRED_VERSION>(url, {
              headers: new HttpHeaders({ [DISABLE_ERROR_ALERT_HEADER]: 'true' }),
            })
            .pipe(
              catchError(() =>
                of<TRESPONSE_APP_UPDATE_MIN_REQUIRED_VERSION>({ minRequiredAppVersion: defaultMinRequiredVersion }),
              ),
            ),
        );

        const isAndroid = this.#platform === 'android';

        if (this.#compareVersions(currentVersionName, minRequiredAppVersion) === 'smaller') {
          if (isAndroid) {
            const res = await AppUpdate.performImmediateUpdate();
            if (res.code === AppUpdateResultCode.CANCELED || res.code === AppUpdateResultCode.FAILED)
              await this.#openAppUpdateModal({ updateType: 'IMMEDIATE', appName: this.#app });
          } else
            await this.#openAppUpdateModal({
              updateType: 'IMMEDIATE',
              appName: this.#app,
              currentVersionName,
              availableVersionName: iosAvailableVersionName ?? '',
            });
        } else if (askForFlexibleUpdates) {
          if (isAndroid) await AppUpdate.startFlexibleUpdate();
          else
            await this.#openAppUpdateModal({
              updateType: 'FLEXIBLE',
              appName: this.#app,
              currentVersionName,
              availableVersionName: iosAvailableVersionName ?? '',
            });
        }
      }
    } catch (error) {
      console.log('[ERROR](AppUpdateService): ', error);
    }
  }

  #getUrl() {
    switch (this.#app) {
      case 'Maintaini8':
        return this.mmEnvironmentService.getUrlFromEnv('GET_MIN_REQUIRED_APP_VERSION');
    }
  }

  #compareVersions(v1: string, v2: string): 'greater' | 'equal' | 'smaller' {
    const [a1, a2, a3] = v1.split('.').map(Number);
    const [b1, b2, b3] = v2.split('.').map(Number);

    if (a1 !== b1) return a1 > b1 ? 'greater' : 'smaller';
    if (a2 !== b2) return a2 > b2 ? 'greater' : 'smaller';
    if (a3 !== b3) return a3 > b3 ? 'greater' : 'smaller';

    return 'equal';
  }

  async #openAppUpdateModal(componentProps: TAppUpdateModalComponentProps) {
    const modal = await this.modalController.create({
      component: AppUpdateModalComponent,
      componentProps,
      cssClass: `app-update-modal--${componentProps.updateType}`,
      canDismiss: componentProps.updateType === 'FLEXIBLE',
    });
    await modal.present();
  }
}
